<template>
  <v-card 
    class="mx-auto"
  > 
    <v-dialog
      v-model="showDeleteLicense"
      max-width="480px"
    >
      <videon-card heading="Delete License" showClose="true" @close="closeDeleteLicenseDialog">
        <v-card-text>
          <p class="subtitle-1 mt-3">
            <span v-if="licensesPendingDelete.length === 1">
              Are you sure you want to delete this license?
            </span>
            <span v-else>
              Are you sure you want to delete
              <strong>{{ licensesPendingDelete.length }}</strong> licenses?
            </span>
          </p>
          <ul v-if="licensesPendingDelete.length > 1" class="mb-3">
            <li
              v-for="license in licensesPendingDelete.slice(0, 5)"
              :key="license.license_id"
            >
              {{ license.license_name || license.license_id }}
            </li>
            <li v-if="licensesPendingDelete.length > 5" class="text--secondary">
              and {{ licensesPendingDelete.length - 5 }} more…
            </li>
          </ul>
          <v-form @submit.prevent="confirmDeleteLicense">
            <v-text-field
              v-model="deleteLicenseReason"
              label="Reason"
              required
              outlined
            ></v-text-field>
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="closeDeleteLicenseDialog"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmDeleteLicense"
                :loading="loading"
              >
                Delete
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showCreateLicense"
      max-width="640px"
      v-if="showCreateLicense"
    >
      <videon-card :heading="(createLicenseType == 'org') ? 'Create Org License' : 'Create Device License'" showClose="true" @close="showCreateLicense = false">
        <v-card-text>
          <v-form @submit.prevent="confirmCreateLicense">
            <div class="text-center">
              <v-btn-toggle
                v-model="createLicenseType"
                divided
                class="mb-5"
                rounded
              >
                <v-btn small value="org">Org License</v-btn>
                <v-btn small value="serial">Device License</v-btn>
              </v-btn-toggle>
            </div>

            <div class="text-center" v-if="createLicenseType == 'org'">
              <v-btn-toggle
                v-model="orgLicenseKind"
                divided
                class="mb-5"
                rounded
              >
                <v-btn small value="cloud_pro">Cloud Pro API</v-btn>
                <v-btn small value="other">Other Entitlement</v-btn>
              </v-btn-toggle>
            </div>
            
            <org-selector 
              v-model="newLicense.org_guid" 
              class="mb-5" 
              v-if="createLicenseType == 'org'"
            />
            
            <v-text-field
              v-if="createLicenseType == 'serial'"
              autocomplete="off"
              v-model="newLicense.serial_number"
              label="Device Serial"
              required
              outlined
            ></v-text-field>

            <cloud-pro-tier-selector
              v-if="createLicenseType == 'org' && orgLicenseKind == 'cloud_pro'"
              v-model="newLicense.profile_guid"
              class="mb-5"
            />

            <profile-selector
              v-else
              v-model="newLicense.profile_guid"
              class="mb-5"
            />
            
            <hr />
            
            <v-text-field
              autocomplete="off"
              v-model="newLicense.license_name"
              label="License Name"
              class="mt-5"
              outlined
              hide-details
            ></v-text-field>
            
            <v-text-field
              autocomplete="off"
              v-model="newLicense.sales_order"
              label="Sales Order"
              class="my-5"
              outlined
              hide-details
            ></v-text-field>

            <div v-if="createLicenseType == 'org'">
              <hr />

              <div v-if="orgLicenseKind == 'cloud_pro'">
                <v-alert dense outlined type="info" class="mb-4">
                  Pick a standard plan (Pro, Pro Plus, Pro Ultra), then set org seats and license details.
                  For one-off call blocks (100K, 500K, etc.), create a custom entitlement profile first, then choose Other Entitlement.
                </v-alert>
                <v-checkbox
                  v-model="newLicense.unlimited"
                  label="Period grant (API top-up, expires this quota period)"
                />
                <v-text-field
                  v-if="!newLicense.unlimited"
                  autocomplete="off"
                  v-model="newLicense.device_count"
                  label="Cloud Pro device seat count (example: 10)"
                  class="mb-5"
                  type="number"
                  min="1"
                  required
                  outlined
                  hide-details
                ></v-text-field>
              </div>

              <div v-else-if="!newLicenseEntitlement || newLicenseEntitlement.profile_platform !== 'cloud_ui'">
                <v-checkbox
                  v-model="newLicense.unlimited"
                  label="Unlimited Devices"
                />
                <v-text-field
                  v-if="!newLicense.unlimited"
                  autocomplete="off"
                  v-model="newLicense.allocation_limit"
                  label="Device License Count (example: 10)"
                  class="mb-5"
                  required
                  outlined
                  hide-details
                ></v-text-field>
              </div>
            </div>
            
            <hr />
            
            <v-checkbox
              class="mt-5 mb-0"
              v-model="shouldExpire"
              label="License Expires"
              hide-details
            />
            
            <div v-if="shouldExpire">
              <v-menu
                v-model="newDatePicker"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="newLicenseTagetDate"
                    label="Expiration date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="newLicenseTagetDate"
                  @input="newDatePicker = false"
                ></v-date-picker>
              </v-menu>
            
              <v-text-field
                class="mt-3"
                autocomplete="off"
                v-model="newLicense.lifespan_days"
                label="License Length (days)"
                type="number"
                outlined
                hide-details
              ></v-text-field>
            </div>
            
            <div class="text-right mt-5">
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmCreateLicense"
              >
                Create License
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showExtendLicense"
      max-width="480px"
      v-if="this.selectedLicense && showExtendLicense"
    >
      <videon-card heading="Extend License" showClose="true" @close="showExtendLicense = false">
        <v-card-text>
          <v-form @submit.prevent="confirmExtendLicense">
            
            <v-text-field
              :value="currentExpiration"
              label="Current expiration date"
              prepend-icon="mdi-calendar"
              readonly
            ></v-text-field>
            
            <v-menu
              v-model="extendDatePicker"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="extendTargetDate"
                  label="New expiration date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="extendTargetDate"
                @input="extendDatePicker = false"
              ></v-date-picker>
            </v-menu>
            
            <v-text-field
              class="mt-5"
              v-model="extendAdditionalDays"
              label="Additional Days"
              type="number"
              required
              outlined
            ></v-text-field>
            
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showExtendLicense = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmExtendLicense"
              >
                Extend
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showAssignLicenseAllocation"
      max-width="90%"
      v-if="this.selectedLicense"
    >
      <videon-card heading="License Allocations" showClose="true" @close="closeManageAllocation">
        <v-card-text>
          <license-allocations :license="this.selectedLicense" @close="closeManageAllocation" />
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-toolbar flat>
      <v-toolbar-title>
        <v-avatar
          height="32px"
          class="mx-auto"
        >
          <videon-logo />
        </v-avatar>
        License Manager
      </v-toolbar-title>
      
      <v-spacer/>
      
      <v-btn
        class="mr-1"
        small
        color="primary"
        outlined
        @click="showCreateLicense = true"
        tooltip="Create License"
      >
        <v-icon>mdi-plus</v-icon> Create License
      </v-btn>
    </v-toolbar>
    
    <v-divider/>
    <search-filter v-model="searchFilters" />
    <v-divider/>
    
    <v-card-text>
      <v-card flat>
        <v-card-title>
          Licenses
        </v-card-title>
        
        <v-card-text>
          <v-alert dense outlined type="error" v-if="error" >
            {{ errorText }}
          </v-alert>
          
          <v-row>
            <v-col cols="12">
              <v-data-table
                v-model="selectedLicenses"
                class="fixHeaders"
                :headers="headers"
                :items="items"
                item-key="license_id"
                show-select
                :expanded.sync="expanded"
                :loading="loading"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
              >
              
                <template v-slot:item.license_name="{ item }">
                  <v-row align="center">
                    <v-icon v-if="item.org_guid">location_city</v-icon>
                    <v-icon v-else-if="item.serial_number">storage</v-icon>
                    <span v-if="item.license_name" class="subtitle-1" >{{ item.license_name }}</span>
                    <span v-else class="subtitle-1 text--disabled" >Unnamed</span>
                  </v-row>
                </template>
                <template v-slot:item.org_guid="{ item }">
                  <span class="subtitle-1">{{ orgName(item) }}</span>
                </template>
                <template v-slot:item.serial_number="{ item }">
                  {{ item.serial_number }}
                </template>
                <template v-slot:item.profile_guid="{ item }">
                  <span class="">{{ entitlementProfileName(item) }}</span>
                </template>
                <template v-slot:item.unlimited="{ item }">
                  {{ licenseTypeLabel(item) }}
                </template>
                <template v-slot:item.profile_platform="{ item }">
                  {{ $helpers.getLabel(item.profile_platform) }}
                </template>
                <template v-slot:item.created="{ item }">
                  <span>{{ item.created | moment("YYYY/MM/DD") }}</span>
                </template>
                <template v-slot:item.expires="{ item }">
                  <span v-if="item.expires" :class="(hasExpired(item)) ? 'red--text' : ''">{{ item.expires | moment("YYYY/MM/DD") }}</span>
                  <span v-else>Never</span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pt-4 pb-3">
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="4">
                        <license-label :license="item"></license-label>
                      </v-col>
                      <v-col cols="5">
                        <strong>License Type:</strong> {{ licenseTypeLabel(item) }}
                      </v-col>
                      <v-col cols="3" class="text-right">
                        <strong>Platform:</strong> {{ $helpers.getLabel(item.profile_platform) }}
                      </v-col>
                    </v-row>
                    
                    <v-row v-if="item.org_guid" class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Organization:</strong> {{ $organizations.getOrg(item.org_guid).displayName() }}
                      </v-col>
                    </v-row>
                    
                    <v-row v-if="item.serial_number" class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Device Serial:</strong> {{ item.serial_number }}
                      </v-col>
                    </v-row>
                    
                    <v-row v-if="item.sales_order" class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Sales Order:</strong> {{ item.sales_order }}
                      </v-col>
                    </v-row>
                    
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Issue Date:</strong>&nbsp; 
                        <span>{{ item.created | moment("YYYY/MM/DD") }}</span>
                      </v-col>
                    </v-row>
                    
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="12" :class="(hasExpired(item)) ? 'red--text' : ''">
                        <strong>Expiration Date:</strong>&nbsp;
                        <span v-if="item.expires">{{ item.expires | moment("YYYY/MM/DD") }} ({{ expirationDays(item.expires) }})</span>
                        <span v-else>Never</span>
                      </v-col>
                    </v-row>
                    
                    <div class="text-right">
                      <v-btn
                        v-if="item.allocation_limit"
                        class="ma-1"
                        small
                        outlined
                        color="primary"
                        @click="manageAllocation(item)"
                      >
                        Manage Allocation
                      </v-btn>
                      <v-btn
                        v-if="item.expires"
                        class="ma-1"
                        small
                        outlined
                        color="secondary"
                        @click="extendLicense(item)"
                      >
                        Extend License
                      </v-btn>
                      <v-btn
                        class="ma-1"
                        small
                        outlined
                        @click="deleteLicense(item)"
                      >
                        Delete License
                      </v-btn>
                    </div>
                  </td>
                </template>
              </v-data-table>
              <v-card-actions v-if="selectedLicenses.length > 0" class="px-0">
                <v-sheet rounded elevation="2" class="py-1 px-3" width="100%">
                  <span class="subtitle-2 mr-2">{{ selectedLicenses.length }} selected</span>
                  <v-btn x-small class="ma-1" outlined @click="openDeleteLicenses(selectedLicenses)">
                    Delete selected
                  </v-btn>
                  <v-btn x-small class="ma-1" text @click="selectedLicenses = []">
                    Clear selection
                  </v-btn>
                </v-sheet>
              </v-card-actions>
              <data-table-pagination v-model="page" :paginationTokens="paginationTokens" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script> 
  import CloudProTierSelector from './components/CloudProTierSelector.vue'
  import ProfileSelector from './components/ProfileSelector.vue'
  import OrgSelector from '../organization/OrgSelector.vue'
  import LicenseAllocations from './LicenseAllocations.vue'
  
  import moment from 'moment'
  
  export default {
    name: 'AdminLicenseManager',
    components: {
      CloudProTierSelector,
      ProfileSelector,
      OrgSelector,
      LicenseAllocations
    },
    data() {
      return {
        error: false,
        errorText: '',
        
        showCreateLicense: false,
        newLicenseTagetDate: '',
        newDatePicker: false,
        createLicenseType: 'org',
        orgLicenseKind: 'cloud_pro',
        newLicense: { 'org_guid': null, 'serial_number': null, 'profile_guid': null, 'license_name': '', 'sales_order': '', 'unlimited': false, 'allocation_limit': null, 'device_count': null, 'lifespan_days': 365},
        shouldExpire: false,
        
        showDeleteLicense: false,
        deleteLicenseReason: '',
        licensesPendingDelete: [],
        selectedLicenses: [],
        
        showEditLicense: false,
        
        showAssignLicenseAllocation: false,
        
        extendDatePicker: false,
        showExtendLicense: false,
        extendTargetDate: '',
        extendAdditionalDays: 365,
        
        selectedLicense: false,
        
        loading: false,
        
        searchFilters: {},
        
        items: [],
        expanded: [],
        
        itemsPerPage: 50,
        page: 1,
        pageCount: 0,
        
        paginationTokens: []
      }
    },
    mounted() {
      this.loadLicenses(0)
    },
    watch: {
      page() {
        this.selectedLicenses = []
        this.loadLicenses(this.page - 1)
      },
      itemsPerPage() {
        this.items = []
        this.page = 1
        this.loadLicenses(this.page - 1)
      },
      
      newLicenseTagetDate() {
        var now = moment()
        var targetDate = moment(this.newLicenseTagetDate)
        this.newLicense.lifespan_days = targetDate.diff(now, 'days') + 1
      },
      
      extendTargetDate() {
        if (this.selectedLicense) {
          var expires = moment(this.selectedLicense.expires)
          var targetDate = moment(this.extendTargetDate)
          this.extendAdditionalDays = targetDate.diff(expires, 'days') + 1
        }
      },
      
      searchFilters() {
        console.log('AdminLicenseManager searchFilters updated', this.searchFilters)
        this.loadLicenses(0)
      },

      orgLicenseKind() {
        this.newLicense.profile_guid = null
        this.newLicense.unlimited = false
        this.newLicense.device_count = null
      },
    },
    computed: {
      headers() {
        var headers = []
        
        headers.push({ text: 'License Name', align: '', value: 'license_name', sortable: true })
        headers.push({ text: 'Sales Order', align: '', value: 'sales_order', sortable: true })
        
        headers.push({ text: 'Entitlement', align: '', value: 'profile_guid', sortable: true })
        
        headers.push({ text: 'Issue Date', align: '', value: 'created', sortable: true })
        headers.push({ text: 'Expiration Date', align: 'center', value: 'expires', sortable: true })
        
        return headers
      },
      
      newLicenseEntitlement() {
        var guid = this.newLicense.profile_guid
        
        if (guid) {
          return this.$entitlementProfiles.getEntitlementProfile(guid)
        }
        
        return false
      },
      
      currentExpiration() {
        if (this.selectedLicense) {
          return moment(this.selectedLicense.expires).format('YYYY-MM-DD')
        }
        return ''
      }
    },
    methods: {
      orgName(item) {
        if (item.org_guid) {
           return this.$organizations.getOrg(item.org_guid).displayName()
        }
        return 'Org GUID Missing'
      }, 
      
      entitlementProfileName(item) {
        if (item.profile_guid) {
           return this.$entitlementProfiles.getEntitlementProfile(item.profile_guid).displayName()
        }
        return 'Profile GUID Missing'
      },
      
      licenseTypeLabel(item) {
        if (item.profile_platform === 'cloud_ui') {
          if (item.unlimited) {
            return 'Cloud Pro API period grant'
          }
          if (item.device_count) {
            return 'Cloud Pro subscription (' + item.device_count + ' seats)'
          }
          return 'Cloud Platform license'
        }
        if (item.unlimited) {
          return 'Unlimited License'
        } else if (item.allocation_limit) {
          return 'Per-Device License (' + item.allocation_limit + ' allocated, ' + item.allocated + ' in use)'
        } else if (item.serial_number) {
          return 'Base License'
        }
        return 'Unknown'
      },
      
      hasExpired(item) {
        var expirationDate = moment(item.expires)
        var todaysdate = moment()
        
        var expirationDays = expirationDate.diff(todaysdate, 'days')
        
        if (expirationDays < 0) {
          return true
        }
        
        return false
      },
      
      expirationDays(expiration) {
        var expirationDate = moment(expiration)
        var todaysdate = moment()
        var expirationDays = expirationDate.diff(todaysdate, 'days')
        
        if (expirationDays < 0) {
          return Math.abs(expirationDays) + ' days ago'
        }
        
        return expirationDays + ' days'
      },
      
      loadLicenses(paginationIndex = 0) {
        console.log('AdminLicenseManager loadLicenses')
        
        this.loading = true
        this.items = []
        
        const paginationToken = this.paginationTokens[paginationIndex - 1]
        
        var queryParams = {
          'pagination_size': this.itemsPerPage
        }
        
        if (paginationToken) {
          queryParams.pagination_token = paginationToken
        }
        
        var filterKeys = Object.keys(this.searchFilters)
        if (filterKeys.length > 0) {
          queryParams = Object.assign(queryParams, this.searchFilters)
          queryParams.show_serials = true
        } else {
          // default show all org & serial licenses...
          queryParams.show_orgs = true
          queryParams.show_serials = true
        }
        
        this.axios.get('/entitlements/licenses', {'params': queryParams}).then((response) => {
          console.log('AdminLicenseManager loadLicenses response', response)
          
          if (response.data.licenses.length > 0) {
            this.items = response.data.licenses
            
            if (response.data.pagination_token) {
              this.$set(this.paginationTokens, paginationIndex, response.data.pagination_token)
            }
          }
          this.loading = false
        }).catch((error) => {
          console.log('AdminLicenseManager loadLicenses error', error)
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      
      createLicense() {
        var expires = moment()
        this.newLicenseTagetDate = expires.add(this.newLicense.lifespan_days, 'days').format('YYYY-MM-DD')
        
        this.showCreateLicense = true
      },
      
      confirmCreateLicense() {
        console.log('AdminLicenseManager confirmCreateLicense', this.newLicense)
        
        var cloudUi = this.orgLicenseKind === 'cloud_pro'
          || (this.newLicenseEntitlement && this.newLicenseEntitlement.profile_platform === 'cloud_ui')
        var orgTargetSpecified = cloudUi
          ? (this.newLicense.unlimited || this.newLicense.device_count)
          : (this.newLicense.unlimited || this.newLicense.allocation_limit)
        // A license's 'target' are the devices that it can be applied to, so for an organization-based license its orgTargetSpecified
        // and for a serial license its the serial number
        var targetSpecified = (this.newLicense.org_guid && orgTargetSpecified) || this.newLicense.serial_number
        
        if (this.newLicense.profile_guid && targetSpecified) {
          var newLicense = { 'profile_guid': this.newLicense.profile_guid } 
          
          if (this.createLicenseType == 'org') {
            newLicense.org_guid = this.newLicense.org_guid
            if (cloudUi) {
              newLicense.unlimited = !!this.newLicense.unlimited
              if (!newLicense.unlimited && this.newLicense.device_count) {
                newLicense.device_count = parseInt(this.newLicense.device_count)
                if (newLicense.device_count <= 0) {
                  console.log('AdminLicenseManager confirmCreateLicense device_count invalid, bailing...', this.newLicense)
                  return
                }
              }
            } else if (this.newLicense.unlimited) {
              newLicense.unlimited = true
            } else if (this.newLicense.allocation_limit) {
              newLicense.allocation_limit = parseInt(this.newLicense.allocation_limit)
              if (newLicense.allocation_limit == 0) {
                console.log('AdminLicenseManager confirmCreateLicense allocation_limit = 0, bailing...', this.newLicense)
                return
              }
            }
          } else {
            newLicense.serial_number = this.newLicense.serial_number
          }
          
          if (this.newLicense.license_name) {
            newLicense.license_name = this.newLicense.license_name
          }
          
          if (this.newLicense.sales_order) {
            newLicense.sales_order = this.newLicense.sales_order
          }
          
          if (this.shouldExpire && this.newLicense.lifespan_days) {
            newLicense.lifespan_days = parseInt(this.newLicense.lifespan_days)
          }
          
          this.loading = true
          
          this.axios.post('/entitlements/licenses', newLicense)
          .then((response) => {
            console.log('AdminLicenseManager confirmCreateLicense response', response)
            this.showCreateLicense = false
            
            this.error = false
            this.errorText = ''
            
            this.loadLicenses(0)
          }).catch((error) => {
            console.log('AdminLicenseManager confirmCreateLicense error', error)
            
            this.error = true
            this.errorText = this.$helpers.parseError(error)
            
            this.loading = false
          })
          
        }
      },
      
      extendLicense(item) {
        console.log('AdminLicenseManager extendLicense', item)
        this.selectedLicense = item
        
        var expires = moment(item.expires)
        this.extendTargetDate = expires.add(this.extendAdditionalDays, 'days').format('YYYY-MM-DD')
        
        this.showExtendLicense = true
      },
      
      confirmExtendLicense() {
        this.loading = true
        this.showExtendLicense = false
        
        this.error = false
        this.errorText = ''
        
        console.log('AdminLicenseManager confirmExtendLicense', this.selectedLicense.license_id, this.extendAdditionalDays)
        
        this.axios.patch('/entitlements/licenses/' + encodeURIComponent(this.selectedLicense.license_id), { 'additional_lifespan_days': parseInt(this.extendAdditionalDays) })
        .then((response) => {
          console.log('AdminLicenseManager confirmExtendLicense response', response)
          this.loadLicenses(0)
        }).catch((error) => {
          console.log('AdminLicenseManager confirmExtendLicense error', error)
        
          this.error = true
          this.errorText = this.$helpers.parseError(error)
        
          this.loading = false
        })
        
      },
      
      openDeleteLicenses(licenses) {
        this.licensesPendingDelete = licenses.filter(license => license.license_id)
        if (this.licensesPendingDelete.length === 0) {
          return
        }
        this.showDeleteLicense = true
      },

      closeDeleteLicenseDialog() {
        this.showDeleteLicense = false
        this.licensesPendingDelete = []
      },

      deleteLicense(item) {
        console.log('AdminLicenseManager deleteLicense', item)
        this.openDeleteLicenses([item])
      },

      async confirmDeleteLicense() {
        if (!this.deleteLicenseReason || this.licensesPendingDelete.length === 0) {
          return
        }

        this.loading = true
        this.error = false
        this.errorText = ''

        const reason = this.deleteLicenseReason
        const results = await Promise.allSettled(
          this.licensesPendingDelete.map((license) => (
            this.axios.delete(
              '/entitlements/licenses/' + encodeURIComponent(license.license_id),
              { data: { reason } }
            )
          ))
        )

        const failed = results.filter((result) => result.status === 'rejected')
        const succeeded = results.length - failed.length

        this.showDeleteLicense = false
        this.deleteLicenseReason = ''
        this.licensesPendingDelete = []
        this.selectedLicenses = []

        if (failed.length === 0) {
          this.error = false
          this.errorText = ''
        } else if (succeeded === 0) {
          this.error = true
          this.errorText = this.$helpers.parseError(failed[0].reason)
        } else {
          this.error = true
          this.errorText = (
            `${succeeded} license(s) deleted, ${failed.length} failed. `
            + this.$helpers.parseError(failed[0].reason)
          )
        }

        this.loadLicenses(0)
      },
      
      manageAllocation(item) {
        this.selectedLicense = item
        this.showAssignLicenseAllocation = true
      },
      
      closeManageAllocation() {
        this.showAssignLicenseAllocation = false
        this.loadLicenses(0)
      }
      
    }
  }
</script>
<style lang="css">
.fixHeaders th {
  min-width: 160px !important;
}
</style>